import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import AuthContext from "@/context/AuthContext";
import authApi from "@/api/authApi";
import ApiError from "@/model/ApiError";
import AuthResponse from "@/model/AuthResponse";
import AuthSession from "@/model/AuthSession";
import UserRole from "@/model/UserRole";

interface AuthProviderProps {
  children: ReactNode;
}

const SESSION_STORAGE_KEY = "host.auth.session";
const ALLOWED_ROLES: UserRole[] = ["HOST", "ADMIN"];

function readStoredSession(): AuthSession | null {
  const raw = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const session = JSON.parse(raw) as Partial<AuthSession> | null;
    if (
      !session ||
      !session.accessToken ||
      !session.email ||
      !session.role ||
      typeof session.expiresAt !== "number"
    ) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    if (session.expiresAt <= Date.now()) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    return session as AuthSession;
  } catch {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    return null;
  }
}

function toSession(response: AuthResponse): AuthSession {
  if (!response.accessToken || !response.email || !response.role) {
    throw new ApiError(500, "Received an incomplete response from the server.");
  }

  if (!ALLOWED_ROLES.includes(response.role)) {
    throw new ApiError(403, "This account cannot access the host portal.");
  }

  return {
    accessToken: response.accessToken,
    email: response.email,
    role: response.role,
    expiresAt: Date.now() + (response.expiresIn ?? 0) * 1000,
  };
}

function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    setSession(readStoredSession());
    setIsInitializing(false);
  }, []);

  const establishSession = useCallback((response: AuthResponse) => {
    const nextSession = toSession(response);
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(nextSession));
    setSession(nextSession);
    return nextSession;
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const response = await authApi.signin({ email, password });
      return establishSession(response);
    },
    [establishSession]
  );

  const signUp = useCallback(
    async (
      firstName: string,
      lastName: string,
      email: string,
      password: string
    ) => {
      const response = await authApi.signup({
        firstName,
        lastName,
        email,
        password,
        role: "HOST",
      });
      return establishSession(response);
    },
    [establishSession]
  );

  const signOut = useCallback(() => {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    setSession(null);
  }, []);

  const value = useMemo(
    () => ({
      session,
      isAuthenticated: session !== null,
      isInitializing,
      signIn,
      signUp,
      signOut,
    }),
    [session, isInitializing, signIn, signUp, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
export type { AuthProviderProps };
